import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ShoppingBag, ArrowRight, Loader2, Package } from 'lucide-react';
import { paymentApi } from '@/lib/api';
import { useCartStore } from '@/lib/store';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation, useCurrency } from '@/hooks/useTranslation';
import { useAppStore } from '@/lib/store';
import type { Order } from '@/types';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('orderId');
  // Providers append ?provider= on their success redirect so we poll the matching status endpoint.
  const provider = searchParams.get('provider') === 'bog' ? 'bog' : 'unipay';
  const [status, setStatus] = useState<'polling' | 'paid' | 'pending' | 'failed'>('polling');
  const [order, setOrder] = useState<Order | null>(null);
  const [orderNumber, setOrderNumber] = useState<string>('');
  const clearCart = useCartStore((s) => s.clearCart);
  const artworks = useAdminStore((s) => s.artworks);
  const settings = useAdminStore((s) => s.settings);
  const { t, localize } = useTranslation();
  const { formatPrice } = useCurrency();
  const storeLanguage = useAppStore((s) => s.language);
  const lang = storeLanguage || (t.nav.home === 'Home' ? 'en' : 'ka');

  // Admin-editable texts with defaults
  const txt = {
    processing: localize(settings?.paymentTexts?.processing) || (lang === 'ka' ? 'გადახდა მუშავდება' : 'Processing Payment'),
    pleaseWait: localize(settings?.paymentTexts?.pleaseWait) || (lang === 'ka' ? 'გთხოვთ დაელოდოთ...' : 'Please wait...'),
    success: localize(settings?.paymentTexts?.success) || (lang === 'ka' ? 'გადახდა წარმატებულია!' : 'Payment Successful!'),
    thankYou: localize(settings?.paymentTexts?.thankYou) || (lang === 'ka' ? 'მადლობა შენაძენისთვის! დადასტურების შეტყობინება მალე მიიღებთ.' : 'Thank you for your purchase! You will receive a confirmation shortly.'),
    myOrders: localize(settings?.paymentTexts?.myOrders) || (lang === 'ka' ? 'ჩემი შეკვეთები' : 'My Orders'),
    continueShopping: localize(settings?.paymentTexts?.continueShopping) || (lang === 'ka' ? 'შოპინგის გაგრძელება' : 'Continue Shopping'),
    purchasedItems: localize(settings?.paymentTexts?.purchasedItems) || (lang === 'ka' ? 'შეძენილი პროდუქტები' : 'Purchased Items'),
    total: localize(settings?.paymentTexts?.total) || (lang === 'ka' ? 'სულ' : 'Total'),
    delivery: localize(settings?.paymentTexts?.delivery) || (lang === 'ka' ? 'მიწოდების დეტალები' : 'Delivery Details'),
    failed: localize(settings?.paymentTexts?.failed) || (lang === 'ka' ? 'გადახდა ვერ მოხერხდა' : 'Payment Failed'),
    failedDesc: localize(settings?.paymentTexts?.failedDesc) || (lang === 'ka' ? 'თქვენი გადახდა ვერ მოხერხდა. თანხა არ ჩამოიჭრა.' : 'Something went wrong. No charges were made.'),
    tryAgain: localize(settings?.paymentTexts?.tryAgain) || (lang === 'ka' ? 'ხელახლა ცდა' : 'Try Again'),
    order: localize(settings?.paymentTexts?.order) || (lang === 'ka' ? 'შეკვეთა' : 'Order'),
  };

  useEffect(() => {
    if (!orderId) { setStatus('failed'); return; }
    clearCart();

    let attempts = 0;
    // Poll for up to ~3 minutes (60 × 3s). UniPay webhook may take time after 3D-Secure (SMS).
    const maxAttempts = 60;
    let stopped = false;

    const poll = async (): Promise<void> => {
      if (stopped) return;
      try {
        let data: { paymentStatus: string; orderNumber?: string };
        if (provider === 'bog') {
          const res = await fetch(`/api/v1/payments/bog/status/${encodeURIComponent(orderId)}`);
          const json = await res.json();
          data = json?.data ?? { paymentStatus: 'pending' };
        } else {
          data = await paymentApi.getStatus(orderId) as { paymentStatus: string; orderNumber?: string };
        }
        if (data.orderNumber) setOrderNumber(data.orderNumber);
        if (data.paymentStatus === 'paid') {
          setStatus('paid');
          try {
            const { authApi } = await import('@/lib/api');
            const orders = await authApi.getMyOrders() as Order[];
            const found = orders.find((o) => o._id === orderId);
            if (found) setOrder(found);
          } catch {}
          return;
        }
        if (data.paymentStatus === 'failed') { setStatus('failed'); return; }
      } catch {}

      attempts++;
      if (attempts >= maxAttempts) {
        // Timeout reached without confirmation. Do NOT show "successful" prematurely.
        // Show "pending" so customer knows status is unconfirmed.
        setStatus('pending');
        return;
      }
      setTimeout(poll, 3000);
    };

    poll();
    return () => { stopped = true; };
  }, [orderId, clearCart, provider]);

  const getArtworkImage = (artworkId: string): string => {
    return artworks.find((a) => a._id === artworkId)?.images?.[0] || '';
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-20">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg w-full">

        {status === 'polling' && (
          <div className="text-center">
            <Loader2 size={48} className="mx-auto text-black/20 dark:text-white/20 animate-spin mb-6" />
            <h1 className="text-2xl font-syncopate font-bold tracking-tighter mb-2">{txt.processing}</h1>
            <p className="text-sm text-black/40 dark:text-white/40">{txt.pleaseWait}</p>
          </div>
        )}

        {status === 'paid' && (
          <div className="text-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}>
              <CheckCircle size={64} className="mx-auto text-green-500 mb-6" />
            </motion.div>
            <h1 className="text-3xl font-syncopate font-bold tracking-tighter mb-2">{txt.success}</h1>
            <p className="text-sm text-black/50 dark:text-white/50 mb-1">{txt.order} {order?.orderNumber || orderNumber || `#${orderId?.slice(0, 8)}`}</p>
            <p className="text-sm text-black/40 dark:text-white/40 mb-8">{txt.thankYou}</p>

            {order?.items?.length ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="mb-8 p-5 bg-[#fafafa] dark:bg-[#0a0a0a] rounded-2xl border border-black/5 dark:border-white/5 text-left">
                <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-4">{txt.purchasedItems}</p>
                <div className="space-y-3">
                  {order.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      {(item.artworkImage || getArtworkImage(item.artworkId)) && (
                        <img src={item.artworkImage || getArtworkImage(item.artworkId)} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0 bg-black/5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate">{localize(item.artworkTitle) || 'Artwork'}</p>
                        <p className="text-[10px] text-black/40 dark:text-white/40">
                          {item.frame !== 'unframed' ? item.frame + ' frame' : lang === 'ka' ? 'ჩარჩოს გარეშე' : 'unframed'} · {lang === 'ka' ? 'რაოდ.' : 'Qty'}: {item.quantity}
                        </p>
                      </div>
                      <p className="text-sm font-bold shrink-0">{formatPrice(item.totalPrice, order.currency)}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-black/5 dark:border-white/5 flex justify-between items-center">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40">{txt.total}</span>
                  <span className="text-lg font-bold">{formatPrice(order.total, order.currency)}</span>
                </div>
              </motion.div>
            ) : null}

            {order?.customer && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                className="mb-8 p-4 bg-black/[0.02] dark:bg-white/[0.02] rounded-xl text-left">
                <div className="flex items-center gap-2 mb-2">
                  <Package size={14} className="text-black/30 dark:text-white/30" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">{txt.delivery}</span>
                </div>
                <p className="text-xs text-black/60 dark:text-white/60">{order.customer.firstName} {order.customer.lastName}</p>
                <p className="text-xs text-black/40 dark:text-white/40">{order.customer.email}</p>
                {order.customer.phone && <p className="text-xs text-black/40 dark:text-white/40">{order.customer.phone}</p>}
              </motion.div>
            )}

            <div className="flex flex-col gap-3">
              <button onClick={() => navigate('/profile?tab=orders')} className="flex items-center justify-center gap-2 w-full py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:opacity-90 transition-opacity">
                <ShoppingBag size={14} /> {txt.myOrders}
              </button>
              <button onClick={() => navigate('/shop')} className="flex items-center justify-center gap-2 w-full py-3.5 border border-black/10 dark:border-white/10 text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:border-black/30 dark:hover:border-white/30 transition-colors">
                {txt.continueShopping} <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {status === 'pending' && (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 size={32} className="text-amber-500 animate-spin" />
            </div>
            <h1 className="text-2xl font-syncopate font-bold tracking-tighter mb-2">
              {lang === 'ka' ? 'გადახდა მუშავდება' : 'Payment Processing'}
            </h1>
            <p className="text-sm text-black/50 dark:text-white/50 mb-1">{txt.order} {orderNumber || `#${orderId?.slice(0, 8)}`}</p>
            <p className="text-sm text-black/40 dark:text-white/40 mb-8 leading-relaxed">
              {lang === 'ka'
                ? 'თქვენი გადახდის სტატუსი ჯერ არ დასტურდება. გადახდის დასრულების შემდეგ მოგივათ შეტყობინება ემეილზე. შეგიძლიათ შეამოწმოთ "My Orders" გვერდი.'
                : 'Your payment status is not yet confirmed. Once finalized, you will receive a confirmation email. You can also check "My Orders" page.'}
            </p>
            <div className="flex flex-col gap-3">
              <button onClick={() => navigate('/profile')} className="w-full py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl flex items-center justify-center gap-2">
                <Package size={14} /> {txt.myOrders}
              </button>
              <button onClick={() => navigate('/shop')} className="w-full py-3.5 border border-black/10 dark:border-white/10 text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl">{txt.continueShopping}</button>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <span className="text-red-500 text-3xl font-bold">!</span>
            </div>
            <h1 className="text-2xl font-syncopate font-bold tracking-tighter mb-2">{txt.failed}</h1>
            <p className="text-sm text-black/40 dark:text-white/40 mb-8">{txt.failedDesc}</p>
            <div className="flex flex-col gap-3">
              <button onClick={() => navigate('/checkout')} className="w-full py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl">{txt.tryAgain}</button>
              <button onClick={() => navigate('/shop')} className="w-full py-3.5 border border-black/10 dark:border-white/10 text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl">{txt.continueShopping}</button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
